import React, { useState, useEffect } from 'react';
import { Users, Pencil, Trash2, UserPlus, Loader2 } from 'lucide-react';
import CreateTeamModal from '../../components/profile/teamManagement/CreateTeamModal';
import TeamMembersModal from '../../components/profile/teamManagement/TeamMembersModal';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTeamMembersModal, setShowTeamMembersModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/team', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch teams');

      const data = await response.json();
      setTeams(data);
    } catch (error) {
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.fetchTeams"),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrUpdateTeam = async (teamId, teamName, employees) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = !!teamId;

      const endpoint = isEditing
        ? `https://www.ia-schedule.com:4139/api/team/${teamId}`
        : 'https://www.ia-schedule.com:4139/api/team';

      const method = isEditing ? 'PUT' : 'POST';

      const body = {
        name: teamName,
        employees: employees.map(emp => ({
          name: emp.name,
          wkl_wrk_load: emp.wkl_wrk_load * 10,
          day_WI: emp.day_WI * 10,
          at_roster_num: 1,
          number: emp.number
        }))
      };

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error(isEditing ? t("alert.error.updateTeam") : t("alert.error.createTeam"));

      if (isEditing) {
        setTeams(teams.map(team =>
          team.id === teamId
            ? { ...team, name: teamName, employees: body.employees }
            : team
        ));
      } else {
        const newTeam = await response.json();
        setTeams([...teams, newTeam]);
      }

      setShowCreateModal(false);
      setEditingTeam(null);
      showAlert({
        title: t("alert.success"),
        description: isEditing ? t("alert.success.updateTeam") : t("alert.success.createTeam"),
        variant: 'success'
      });
    } catch (error) {
      showAlert({
        title: t("alert.error"),
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm(t("alert.sureDelete"))) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/team/${teamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error(t("alert.error.deleteTeam"));

      setTeams(teams.filter(team => team.id !== teamId));
      showAlert({
        title: t("alert.success"),
        description: t("alert.success.deleteTeam"),
        variant: 'success'
      });
    } catch (error) {
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.deleteTeam"),
        variant: 'destructive'
      });
    }
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setShowCreateModal(true);
  };

  const handleViewTeamMembers = (team) => {
    setSelectedTeam(team);
    setShowTeamMembersModal(true);
  };

  return (
    <div className="-mt-16 min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("teams.title")}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {t("teams.description")}
            </p>
          </div>
          <button
            onClick={() => {
              setEditingTeam(null);
              setShowCreateModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shadow-sm"
          >
            <UserPlus className="w-5 h-5" />
            {t("teams.createButton")}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : teams.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">{t("teams.empty.title")}</h3>
            <p className="mt-1 text-sm text-gray-500 mb-4">
              {t("teams.empty.description")}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <UserPlus className="w-5 h-5" />
              {t("teams.empty.button")}
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {teams.map(team => (
              <div
                key={team.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-indigo-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {team.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {team.employees.length} {t("teams.count")}{team.employees.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleViewTeamMembers(team)}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        {t("teams.viewMembers")}
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditTeam(team)}
                          className="p-2 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-gray-100"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team.id)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreateModal && (
          <CreateTeamModal
            onClose={() => {
              setShowCreateModal(false);
              setEditingTeam(null);
            }}
            onCreate={handleCreateOrUpdateTeam}
            editTeam={editingTeam}
          />
        )}

        {showTeamMembersModal && selectedTeam && (
          <TeamMembersModal
            team={selectedTeam}
            onClose={() => setShowTeamMembersModal(false)}
            translations={t}
          />
        )}
      </div>
    </div>
  );
};

export default TeamManagement;